import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import ChatList from './ChatList';
import ChatRoom from './ChatRoom';
import createApiClient from '@/api/axiosInstance';
import {
  connectWebSocket,
  disconnectWebSocket,
  unsubscribe,
} from '@/api/chatService';

const ChatWidget = () => {
  const [chatList, setChatList] = useState([]);
  const { user_type_id, role } = useSelector(
    (state) => state.authReducer.userData,
  );
  const [isInboxOpen, setIsInboxOpen] = useState(false);
  const [openChats, setOpenChats] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notificationSubscription, setNotificationSubscription] =
    useState(null);
  const [projects, setProjects] = useState([]);

  const senderId = user_type_id;
  let senderType = role.toUpperCase();
  if (senderType === 'SUPPLIER') senderType = 'MANUFACTURER';

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const api = createApiClient(navigate, dispatch);

  // Fetch chat list
  const fetchChats = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/api/chats/rooms/${senderType}/${senderId}`,
      );
      const chats = response.data.map((chat) => ({
        id: chat.counterpartUserId,
        name: chat.counterpartName,
        lastMessage: chat.lastMessageContentSnippet
          ? `${chat.lastMessageSenderName}: ${chat.lastMessageContentSnippet}`
          : 'No messages yet',
        avatar: chat.counterpartImageUrl || 'https://i.pravatar.cc/40',
        time: chat.lastMessageTimestamp ? chat.lastMessageTimestamp : '',
        unread: chat.unreadCount || 0,
        roomId: chat.id,
        projectId: chat.projectId,
      }));
      setChatList(chats);

      // Extract unique projects from chats
      const uniqueProjects = [
        ...new Set(
          chats.filter((chat) => chat.projectId).map((chat) => chat.projectId),
        ),
      ].map((id) => ({
        id,
        name: `Project ${id}`, // Replace with actual project name if available
      }));
      setProjects(uniqueProjects);
    } catch (err) {
      console.error('Error fetching chat list:', err);
      setError('Failed to load chats. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize WebSocket connection
  const initializeWebSocket = () => {
    connectWebSocket((client) => {
      const subscription = client.subscribe(
        `/topic/notifications/${senderType}/${senderId}`,
        (notification) => {
          const data = JSON.parse(notification.body);
          if (data.type === 'NEW_MESSAGE') {
            fetchChats();
          }
        },
      );
      setNotificationSubscription(subscription.id);
    });
  };

  // Setup initial WebSocket connection when component mounts
  useEffect(() => {
    // Connect to WebSocket if there are open chats or inbox is open
    if (openChats.length > 0 || isInboxOpen) {
      if (!notificationSubscription) {
        initializeWebSocket();
      }
    }

    return () => {
      // Only disconnect if there are no open windows
      if (notificationSubscription && openChats.length === 0 && !isInboxOpen) {
        unsubscribe(notificationSubscription);
        setNotificationSubscription(null);
        disconnectWebSocket();
      }
    };
  }, [openChats, isInboxOpen]);

  // Fetch chats when inbox is opened
  useEffect(() => {
    if (isInboxOpen) {
      fetchChats();
    }
  }, [isInboxOpen]);

  // Refresh chat list periodically when inbox is open
  useEffect(() => {
    let interval;
    if (isInboxOpen) {
      interval = setInterval(fetchChats, 30000); // Refresh every 30 seconds
    }
    return () => clearInterval(interval);
  }, [isInboxOpen]);

  // Handle chat selection from inbox
  const handleChatSelect = (chat) => {
    // If chat is already open, don't open it again
    if (!openChats.some((c) => c.roomId === chat.roomId)) {
      setOpenChats((prev) => [chat, ...prev]);
    }

    // Update unread count optimistically in UI
    setChatList((prev) =>
      prev.map((c) => (c.roomId === chat.roomId ? { ...c, unread: 0 } : c)),
    );
  };

  // Close specific chat
  const closeChat = (roomId) => {
    setOpenChats((prev) => prev.filter((chat) => chat.roomId !== roomId));
  };

  // Total unread count for notification badge
  const totalUnread = chatList.reduce((sum, chat) => sum + chat.unread, 0);

  return (
    <div className="fixed bottom-0 right-4 z-50 flex flex-row-reverse">
      {isInboxOpen && (
        <div className="bg-white h-[calc(100vh-200px)] rounded-t-lg shadow-lg ml-2">
          <ChatList
            chatList={chatList}
            isLoading={isLoading}
            error={error}
            projects={projects}
            onSelectChat={handleChatSelect}
            onClose={() => setIsInboxOpen(false)}
            onRefresh={fetchChats}
          />
        </div>
      )}

      {/* Open Chat Rooms */}
      {openChats.map((chat) => (
        <ChatRoom
          chat={chat}
          onBack={() => closeChat(chat.roomId)}
          senderId={senderId}
          senderType={senderType}
          standalone={true}
        />
      ))}

      {/* Inbox Toggle Button */}
      {!isInboxOpen && (
        <button
          onClick={() => setIsInboxOpen((prev) => !prev)}
          className="bg-purple-200 w-[320px] text-gray-800 px-4 py-2 rounded-t-lg flex justify-between items-center gap-2 transition-all ease-in duration-300 relative shadow-md hover:bg-purple-300"
        >
          <span className="text-base font-medium">Messages</span>
          {totalUnread > 0 && (
            <div className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
              {totalUnread > 9 ? '9+' : totalUnread}
            </div>
          )}
          {isInboxOpen ? (
            <ChevronDown className="h-6 w-6 font-light" />
          ) : (
            <ChevronUp className="h-6 w-6 font-light" />
          )}
        </button>
      )}
    </div>
  );
};

export default ChatWidget;
